import React from "react";

import {
  mdiCashMultiple,
  mdiContentCopy,
  mdiEarth,
  mdiPackageVariant,
} from "@mdi/js";
import Icon from "@mdi/react";
import { useTranslation } from "react-i18next";

import FastChannelEPG from "./FastChannelEPG";
import useCopyToClipboard from "../../../hooks/useCopyToClipboard";
import { prettifyDistributionFormat } from "../../../utils/fast";
import Block from "../../block/Block";
import Button, { BUTTON_TYPES } from "../../button/Button";
import ReadOnlyField from "../../form/ReadOnlyField";
import SimplePlayer from "../../player/SimplePlayer";

import type { JSX } from "react";

import type {
  OVPBroadcast,
  ScheduleBroadcast,
} from "../../../api/FastAPI.types";

interface IBroadcastInformations {
  broadcast?: ScheduleBroadcast | OVPBroadcast;
  scheduleHlsUrl?: string | null;
  activeScheduleId: string;
}

function BroadcastInformations({
  broadcast,
  scheduleHlsUrl,
  activeScheduleId,
}: IBroadcastInformations): JSX.Element {
  const { t } = useTranslation("editor-fast-channel");
  const copyToClipboard = useCopyToClipboard();

  const handleCopyEpgUrl = async (): Promise<void> => {
    if (!broadcast?.epg_url) return;
    await copyToClipboard({
      value: broadcast.epg_url,
      successMessage: t("copyEpgUrlSuccess", { name: broadcast.name }),
      errorMessage: t("copyEpgUrlError", { name: broadcast.name }),
    });
  };

  const buttonCopyEpgUrl = broadcast?.epg_url ? (
    <Button
      onClick={handleCopyEpgUrl}
      size="md"
      type={BUTTON_TYPES.BUTTON}
      variant="secondary"
    >
      <Icon path={mdiContentCopy} size={0.875} />
      {t("copyEpgUrl")}
    </Button>
  ) : undefined;

  const isOVP = broadcast?.type === "ovp";

  return (
    // w-0 is mandatory for the Timeline resize to work
    <div className="flex w-0 flex-1 flex-col gap-[0.78125rem]">
      {isOVP ? (
        <Block title={t("feed_preview")}>
          <div className="mx-auto aspect-video max-h-125">
            <SimplePlayer isPlaying src={broadcast?.embed_url} />
          </div>
        </Block>
      ) : (
        <React.Fragment>
          <Block
            className="relative"
            headerComponent={buttonCopyEpgUrl}
            title={t("programGuideOverview")}
            zIndex={1}
          >
            {activeScheduleId ? (
              <FastChannelEPG activeScheduleId={activeScheduleId} />
            ) : null}
          </Block>
          <Block title={t("technical_feed_preview")}>
            <div className="flex gap-6.25">
              <div className="size-full flex-1">
                <SimplePlayer isPlaying isResponsive src={scheduleHlsUrl} />
              </div>
              <div className="size-full flex-1">
                <SimplePlayer
                  isPlaying
                  isResponsive
                  src={broadcast?.broadcast_url}
                />
              </div>
            </div>
          </Block>
        </React.Fragment>
      )}

      <Block title={t("platform_delivery_information")}>
        <div className="flex flex-col">
          <ReadOnlyField
            isUpperCase
            icon={mdiPackageVariant}
            label={t("distribution_format")}
            value={prettifyDistributionFormat(broadcast?.distribution_format)}
          />
          <ReadOnlyField
            isUpperCase
            icon={mdiCashMultiple}
            label={t("ad_provider")}
            value={broadcast?.ad_service}
          />
          <ReadOnlyField
            isUpperCase
            icon={mdiEarth}
            label={t("cdn_provider")}
            value={broadcast?.output_network}
          />
        </div>
      </Block>
    </div>
  );
}

export default BroadcastInformations;
